import React, { useEffect, useRef, useState } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom'; // Uncomment if navigation is needed

// Firebase Imports (ensure '../firebase.js' is configured)
import { db, Storage, auth } from '../firebase'; // Adjust path as needed
import { ref as storageRef, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { setDoc, doc, collection, serverTimestamp, addDoc } from 'firebase/firestore';

// UI Imports
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PPI_SCALE = 60; // Pixels Per Inch for display

const ThankYouCardEditor = () => {
    const canvasRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const fabricCanvasRef = useRef(null);
    // const navigate = useNavigate(); // For redirection after save

    // UI State
    const [loading, setLoading] = useState(false); // General loading for file processing
    const [isSaving, setIsSaving] = useState(false); // Specific for product save operation
    const [librariesLoaded, setLibrariesLoaded] = useState(false);

    // Content State
    const [currentFile, setCurrentFile] = useState(null); // Original uploaded file (PDF or image)
    const [contentImageUrl, setContentImageUrl] = useState(null); // Data URL for canvas display
    const [mockupImageUrl, setMockupImageUrl] = useState(null); // Live mockup data URL (not used for saving, generated on save)
    const [modifications, setModifications] = useState([]);
    const [currentImageSize, setCurrentImageSize] = useState({ width: 0, height: 0 }); // Actual design dimensions in inches

    // Product Information
    const [productName, setProductName] = useState('');
    const productNameRef = useRef(''); // To capture product name at time of save

    // Card settings
    const [cardColor, setCardColor] = useState('white');
    const [cardOrientation, setCardOrientation] = useState('landscape');

    // Pricing state
    const [baseCost, setBaseCost] = useState(0);
    const [printCost, setPrintCost] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const thankYouCardImages = {
        white: '/thank-you-card.jpg',
        cream: '/thank-you-card.jpg',
        black: '/thank-you-card.jpg'
    };

    const cardConfigs = {
        landscape: {
            width: 6 * 72, height: 3.5 * 72, label: '6" x 3.5"', basePrice: 7, printPrice: 0,
            printArea: { x: 0.48 * 80, y: 0.3 * 80, width: (6) * PPI_SCALE, height: (3.5) * PPI_SCALE }
        },
    };

    useEffect(() => {
        productNameRef.current = productName;
    }, [productName]);

    const calculateBaseCost = (orientation) => cardConfigs[orientation]?.basePrice || 0;
    const calculatePrintCost = (orientation, hasDesign) => hasDesign ? (cardConfigs[orientation]?.printPrice || 0) : 0;
    const calculateTotalPrice = (base, print) => base + print;

    useEffect(() => {
        const newBaseCost = calculateBaseCost(cardOrientation);
        const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
        const newPrintCost = calculatePrintCost(cardOrientation, hasContent);
        const newTotalPrice = calculateTotalPrice(newBaseCost, newPrintCost);
        setBaseCost(newBaseCost); setPrintCost(newPrintCost); setTotalPrice(newTotalPrice);
    }, [cardOrientation, contentImageUrl, modifications, librariesLoaded]);

    const updateMockupImageForDisplay = () => { // Renamed to avoid confusion with saving mockup
        if (!fabricCanvasRef.current) return;
        const canvas = fabricCanvasRef.current;
        const guide = canvas.getObjects().find(obj => obj.isClipRectGuide);
        const guideProps = guide ? { stroke: guide.stroke, strokeWidth: guide.strokeWidth } : null;
        if (guide) guide.set({ stroke: 'transparent', strokeWidth: 0 });
        canvas.renderAll();
        const dataURL = canvas.toDataURL({ format: 'png', quality: 0.8 });
        if (guide && guideProps) guide.set(guideProps);
        canvas.renderAll();
        setMockupImageUrl(dataURL); // This is for the UI mockup if you show it, not for DB save
    };

    // Library Loading
    useEffect(() => {
        const loadScript = (src) => new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
            const script = document.createElement('script');
            script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
            document.head.appendChild(script);
        });
        const loadLibraries = async () => {
            setLoading(true);
            try {
                if (!window.pdfjsLib) {
                    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
                    if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                    else throw new Error("pdf.js failed to load.");
                }
                if (!window.fabric) {
                    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js');
                    if (!window.fabric) throw new Error("Fabric.js failed to load.");
                }
                setLibrariesLoaded(true);
            } catch (error) {
                console.error('Error loading libraries:', error);
                toast.error(`Libs load error: ${error.message}. Refresh.`);
            } finally { setLoading(false); }
        };
        loadLibraries();
    }, []);

    // Canvas Initialization & Event Handlers
    useEffect(() => {
        if (!librariesLoaded || !canvasRef.current || fabricCanvasRef.current) return;
        const canvas = new window.fabric.Canvas(canvasRef.current);
        fabricCanvasRef.current = canvas;

        const updateAndLogImageSize = () => {
            const currentCanvas = fabricCanvasRef.current;
            if (!currentCanvas) return;
            let contentObject = currentCanvas.getActiveObject() || currentCanvas.getObjects().find(obj => obj.isContent === true);
            if (contentObject) {
                const scaledWidthPx = contentObject.getScaledWidth();
                const scaledHeightPx = contentObject.getScaledHeight();
                const widthInches = (scaledWidthPx / PPI_SCALE).toFixed(2);
                const heightInches = (scaledHeightPx / PPI_SCALE).toFixed(2);
                setCurrentImageSize({ width: parseFloat(widthInches), height: parseFloat(heightInches) });
            } else {
                setCurrentImageSize({ width: 0, height: 0 });
            }
        };

        const updateModifications = (obj) => {
            const mod = { id: obj.id || `obj_${Date.now()}`, type: obj.type, left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle, ...(obj.type === 'i-text' && { text: obj.text, fontFamily: obj.fontFamily, fill: obj.fill, fontSize: obj.fontSize })};
            if (!obj.id) obj.set('id', mod.id);
            setModifications(prev => {
                const idx = prev.findIndex(m => m.id === mod.id);
                if (idx > -1) { const upd = [...prev]; upd[idx] = mod; return upd; }
                return [...prev, mod];
            });
        };

        const commonEventHandler = (e) => {
            if (e.target && e.target.isContent) updateModifications(e.target);
            setTimeout(() => { updateMockupImageForDisplay(); updateAndLogImageSize(); }, 50);
        };
        canvas.on({ 'object:modified': commonEventHandler, 'object:added': commonEventHandler, 'object:removed': commonEventHandler, 'object:scaling': updateAndLogImageSize, 'path:created': commonEventHandler });
        
        return () => { fabricCanvasRef.current?.dispose(); fabricCanvasRef.current = null; };
    }, [librariesLoaded]);

    const setupContentClipping = (canvas, printAreaConfig) => {
        if (!canvas || !printAreaConfig) return;
        const clipPath = new window.fabric.Rect({ left: printAreaConfig.x, top: printAreaConfig.y, width: printAreaConfig.width, height: printAreaConfig.height, absolutePositioned: true });
        canvas.getObjects().filter(o => o.isContent).forEach(obj => obj.clipPath = clipPath);
        canvas.requestRenderAll();
    };

    const updateCardCanvasView = (canvas, cardConfig, cardBgKey) => {
        if (!canvas || !cardConfig) return;
        canvas.setDimensions({ width: cardConfig.width, height: cardConfig.height });
        const imageUrl = thankYouCardImages[cardBgKey];
        if (imageUrl) {
            window.fabric.Image.fromURL(imageUrl, (img) => {
                if (!img) { canvas.setBackgroundColor(cardBgKey, canvas.renderAll.bind(canvas)); return; }
                const cw = canvas.getWidth(), ch = canvas.getHeight();
                const scale = Math.max(cw / img.width, ch / img.height);
                img.set({ originX: 'left', originY: 'top', left: (cw - img.width * scale) / 2, top: (ch - img.height * scale) / 2, scaleX: scale, scaleY: scale });
                canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
            }, { crossOrigin: 'anonymous' });
        } else { canvas.setBackgroundColor(cardBgKey, canvas.renderAll.bind(canvas)); }

        let guide = canvas.getObjects().find(obj => obj.isClipRectGuide);
        if (guide) canvas.remove(guide);
        const pa = cardConfig.printArea;
        guide = new window.fabric.Rect({ left: pa.x, top: pa.y, width: pa.width, height: pa.height, fill: 'transparent', stroke: 'rgba(0,102,204,0.7)', strokeWidth: 1, strokeDashArray: [4, 4], selectable: false, evented: false, isClipRectGuide: true });
        canvas.add(guide).sendToBack(guide);
        setupContentClipping(canvas, pa);
        canvas.getObjects().filter(o => o.isContent).forEach(obj => repositionContentWithinPrintArea(obj, canvas, pa));
        canvas.renderAll();
        setTimeout(() => { updateMockupImageForDisplay(); }, 100);
    };

    const repositionContentWithinPrintArea = (obj, canvas, pa) => {
        if (!obj || !canvas || !pa) return;
        const scale = Math.min((pa.width * 0.9) / obj.width, (pa.height * 0.9) / obj.height, 1);
        obj.scale(scale);
        obj.set({ left: pa.x + (pa.width - obj.getScaledWidth()) / 2, top: pa.y + (pa.height - obj.getScaledHeight()) / 2 }).setCoords();
    };

    useEffect(() => {
        const c = fabricCanvasRef.current;
        if (!c || !librariesLoaded) return;
        updateCardCanvasView(c, cardConfigs[cardOrientation], cardColor);
        window.dispatchEvent(new Event('resize'));
    }, [cardOrientation, cardColor, librariesLoaded]);

    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || !contentImageUrl || !librariesLoaded) return;
        canvas.getObjects().filter(o => o.isContent && o.isUploadedImage).forEach(obj => canvas.remove(obj));
        window.fabric.Image.fromURL(contentImageUrl, (img) => {
            if (!img) { console.error("Failed to load image from URL:", contentImageUrl); return; }
            img.set({ isContent: true, isUploadedImage: true, selectable: true, id: `uploaded_img_${Date.now()}` });
            const pa = cardConfigs[cardOrientation].printArea;
            if (pa) img.clipPath = new window.fabric.Rect({ left: pa.x, top: pa.y, width: pa.width, height: pa.height, absolutePositioned: true });
            canvas.add(img);
            repositionContentWithinPrintArea(img, canvas, pa);
            setupContentClipping(canvas, pa);
            canvas.setActiveObject(img).renderAll();
            setTimeout(() => { // Ensure size is updated for this new image
                const sw = img.getScaledWidth(), sh = img.getScaledHeight();
                setCurrentImageSize({ width: parseFloat((sw / PPI_SCALE).toFixed(2)), height: parseFloat((sh / PPI_SCALE).toFixed(2)) });
            }, 100);
        }, { crossOrigin: 'anonymous' });
    }, [contentImageUrl, librariesLoaded, cardOrientation]);

    const handleFileChange = async (e) => {
        if (!librariesLoaded) { toast.warn('Libraries loading. Try again.'); return; }
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true); setCurrentFile(file);
        try {
            let imageURL;
            if (file.type === 'application/pdf') {
                if (!window.pdfjsLib) throw new Error('PDF library not available.');
                imageURL = await renderPDFToImageURL(file);
            } else if (file.type.startsWith('image/')) {
                imageURL = await readFileToDataURL(file);
            } else { throw new Error('Unsupported file type.'); }
            setContentImageUrl(imageURL);
            toast.success("File processed.");
        } catch (err) {
            console.error('Error processing file:', err);
            toast.error(`File load error: ${err.message}`);
        } finally {
            setLoading(false);
            if (e.target) e.target.value = null;
        }
    };

    const renderPDFToImageURL = async (file) => { // This is convertPdfToImage
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2.0 });
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = viewport.width; tempCanvas.height = viewport.height;
        const ctx = tempCanvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport: viewport }).promise;
        return tempCanvas.toDataURL('image/png');
    };

    const readFileToDataURL = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject; reader.readAsDataURL(file);
    });

    const deleteSelected = () => {
        const c = fabricCanvasRef.current; if (!c) return;
        c.getActiveObjects().filter(o => o.isContent).forEach(obj => c.remove(obj));
        c.discardActiveObject().renderAll();
    };

    const clearCanvasContent = () => {
        const c = fabricCanvasRef.current; if (!c) return;
        c.getObjects().filter(obj => obj.isContent).forEach(obj => c.remove(obj));
        c.discardActiveObject().renderAll();
        setContentImageUrl(null); setModifications([]); setCurrentImageSize({ width: 0, height: 0 });
        setTimeout(() => { updateMockupImageForDisplay(); }, 50);
    };

    const addText = () => {
        const c = fabricCanvasRef.current; if (!c || !librariesLoaded) return;
        const pa = cardConfigs[cardOrientation].printArea; if (!pa) { toast.error("Print area not defined."); return; }
        const text = new window.fabric.IText('Your Text', { fontFamily: 'Arial', fontSize: Math.max(12, Math.min(24, pa.width / 15)), fill: cardColor === 'black' ? '#FFF' : '#333', textAlign: 'center', originX: 'center', originY: 'center', isContent: true, id: `text_${Date.now()}` });
        text.set({ left: pa.x + pa.width / 2, top: pa.y + pa.height / 2 });
        c.add(text); repositionContentWithinPrintArea(text, c, pa); setupContentClipping(c, pa);
        c.setActiveObject(text).renderAll();
    };
    
    // Responsive Scaling
    useEffect(() => {
        const container = canvasContainerRef.current;
        const canvasEl = fabricCanvasRef.current?.wrapperEl;
        if (!container || !canvasEl || !librariesLoaded) return;
        const observer = new ResizeObserver(entries => {
            if (!fabricCanvasRef.current) return;
            const fabCan = fabricCanvasRef.current;
            const cWidth = entries[0].contentRect.width;
            const logWidth = fabCan.getWidth();
            let scale = 1;
            if (logWidth > 0 && cWidth < logWidth) scale = cWidth / logWidth;
            canvasEl.style.transform = `scale(${scale})`;
            canvasEl.style.transformOrigin = 'top left';
            container.style.height = `${fabCan.getHeight() * scale}px`;
        });
        observer.observe(container);
        // Initial call after a short delay to ensure canvas is sized
        const timer = setTimeout(() => {
            if (fabricCanvasRef.current && container.clientWidth > 0) {
                 const fabCan = fabricCanvasRef.current;
                 const logWidth = fabCan.getWidth();
                 let scale = 1;
                 if (logWidth > 0 && container.clientWidth < logWidth) scale = container.clientWidth / logWidth;
                 canvasEl.style.transform = `scale(${scale})`;
                 canvasEl.style.transformOrigin = 'top left';
                 container.style.height = `${fabCan.getHeight() * scale}px`;
            }
        }, 200);
        return () => { observer.disconnect(); clearTimeout(timer); }
    }, [librariesLoaded]);


    // --- Firebase Save Logic ---

    // Function 3: saveCanvasToStorage (adapted for Thank You Cards)
    const saveCanvasMockupToStorage = async (canvasInstance, designId, userId) => {
        if (!canvasInstance) throw new Error("Canvas not available for mockup.");

        const guide = canvasInstance.getObjects().find(obj => obj.isClipRectGuide);
        const guideProps = guide ? { stroke: guide.stroke, strokeWidth: guide.strokeWidth, fill: guide.fill } : null;
        if (guide) guide.set({ stroke: 'transparent', strokeWidth: 0, fill: 'transparent' }); // Hide for export

        canvasInstance.renderAll();
        // Export at a decent resolution for mockup, multiplier can be adjusted
        const dataURL = canvasInstance.toDataURL({ format: 'png', quality: 0.9, multiplier: 1.5 });

        if (guide && guideProps) guide.set(guideProps); // Restore guide
        canvasInstance.renderAll();

        if (!dataURL) throw new Error("Failed to generate mockup image data.");

        const sanProductName = (productNameRef.current.replace(/\s+/g, '_') || 'untitled_thankyoucard').substring(0,50);
        // Path: BrandingMockups/ThankYouCards/{userId}/{designId}_mockup.png
        const filePath = `BrandingMockups/ThankYouCards/${designId}_mockup.png`;
        const fileRef = storageRef(Storage, filePath);

        await uploadString(fileRef, dataURL, 'data_url');
        return await getDownloadURL(fileRef);
    };
    
    // Helper to upload original PDF (if one was provided)
    const uploadOriginalPdfToStorage = async (pdfFile, designId, userId) => {
        if (!pdfFile || pdfFile.type !== 'application/pdf') return null;
        
        const sanProductName = (productNameRef.current.replace(/\s+/g, '_') || 'untitled_thankyoucard').substring(0,50);
        // Path: BrandingImages/ThankYouCards/{userId}/{designId}_original.pdf
        const filePath = `BrandingImages/ThankYouCards/${designId}_original.pdf`;
        const fileRef = storageRef(Storage, filePath);
        
        await uploadBytes(fileRef, pdfFile);
        return await getDownloadURL(fileRef);
    };

    // Function 4: handleSaveProduct
    const handleSaveProduct = async () => {
        if (!auth.currentUser) {
            toast.error("Please log in to save your design.");
            return;
        }
        if (!productName.trim()) {
            toast.error("Please enter a Product Name for your design.");
            return;
        }
        const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
        if (!hasContent) {
            toast.error("Please add a design or text to your card before saving.");
            return;
        }

        setIsSaving(true);
        const savingToastId = toast.loading("Saving your Thank You Card...");

        const userId = auth.currentUser.uid;
        // Create a new document ref in Firestore to get an auto-generated ID first
        const designDocRef = doc(collection(db, "users", userId, "BrandingDesigns"));
        const designId = designDocRef.id;

        let originalPdfUrl = null;
        let mockupImageUrlResult = null;

        try {
            // 1. Upload Original PDF (if currentFile is a PDF)
            if (currentFile && currentFile.type === 'application/pdf') {
                toast.update(savingToastId, { render: "Uploading original PDF..." });
                originalPdfUrl = await uploadOriginalPdfToStorage(currentFile, designId, userId);
            }

            // 2. Generate and Upload Mockup from Canvas
            toast.update(savingToastId, { render: "Generating card mockup..." });
            if (!fabricCanvasRef.current) throw new Error("Canvas is not ready for mockup generation.");
            mockupImageUrlResult = await saveCanvasMockupToStorage(fabricCanvasRef.current, designId, userId);

            // 3. Prepare Firestore Payload
const payload = {
  designName: productName.trim(),
  totalPrice: totalPrice,

  frontDesign: originalPdfUrl, 

  size: cardConfigs[cardOrientation].label, 
  color: cardColor, 

  images: {
    front: mockupImageUrlResult,
  },

  costs: {
    front: printCost,
  },

  Printsizes: {
    front:
      currentImageSize?.width > 0 && currentImageSize?.height > 0
        ? currentImageSize
        : null,
  },

  productName: "ThankYouCard", // Or dynamic if using from a product object
  SKU: `TYC-${designId}`,
  baseCost: baseCost,
  PrintCost: printCost,
  timestamp: serverTimestamp(),
  userId: userId,

  // Optional metadata
  // fabricCanvasState: fabricCanvasRef.current?.toJSON([...]),
  // designModifications: modifications,
};


            console.log("Saving payload:", payload);

            // 4. Save to Firestore
            toast.update(savingToastId, { render: "Saving design details..." });
            await setDoc(designDocRef, payload);

            toast.update(savingToastId, { render: "Thank You Card saved successfully!", type: "success", isLoading: false, autoClose: 5000 });
            // navigate('/my-designs'); // Optional: redirect user

        } catch (error) {
            console.error("Error saving Thank You Card:", error);
            toast.update(savingToastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 7000 });
        } finally {
            setIsSaving(false);
        }
    };

    // --- END Firebase Save Logic ---

    const ControlCard = ({ title, children }) => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
            {children}
        </div>
    );

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            <ToastContainer position="top-right" theme="colored" />
            <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-10">
                <div className="max-w-screen-2xl mx-auto px-6 py-1">
                    <h1 className="text-2xl font-bold text-gray-900">Thank You Card Designer</h1>
                </div>
            </header>
            <main className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-hidden">
                <aside className="col-span-12 lg:col-span-3 flex flex-col gap-6 overflow-y-auto pb-4">
                    <ControlCard title="1. Upload Your Design">
                        <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg" // Accept common image types too now
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                            disabled={!librariesLoaded || loading || isSaving}
                        />
                        {!librariesLoaded && <div className="mt-2 p-2 text-xs bg-yellow-100 text-yellow-800 rounded">Loading libraries...</div>}
                         {loading && <div className="mt-2 p-2 text-xs bg-blue-100 text-blue-800 rounded">Processing...</div>}
                    </ControlCard>
                    <div title="2. Customize Your Card">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="tycProductName" className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                <input id="tycProductName" type="text" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g., Spring Sale Thanks" className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" disabled={isSaving} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Card Background</label>
                                <div className="flex space-x-2">
                                    {Object.keys(thankYouCardImages).map(colorKey => (
                                        <button 
                                            key={colorKey}
                                            onClick={() => setCardColor(colorKey)} 
                                            className={`flex-1 py-2 text-sm rounded-md border-2 capitalize ${cardColor === colorKey ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold' : 'border-gray-300 bg-gray-200'} hover:border-blue-400 disabled:opacity-50`}
                                            disabled={isSaving}
                                        >
                                            {colorKey}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <ControlCard title="Current Design Size">
                        {currentImageSize.width > 0 && currentImageSize.height > 0 ? (
                            <div className="text-sm text-gray-600">
                                <span className="font-medium text-gray-800">{currentImageSize.width}" W × {currentImageSize.height}" H</span>
                                <p className="text-xs text-gray-500 mt-1">Within printable area of {cardConfigs[cardOrientation].printArea.width/PPI_SCALE}" × {cardConfigs[cardOrientation].printArea.height/PPI_SCALE}".</p>
                            </div>
                        ) : ( <div className="text-sm text-gray-500 text-center py-2">No active design element or size not determined.</div> )}
                    </ControlCard>
                </aside>
                <section className="col-span-12 lg:col-span-5 flex flex-col bg-white rounded-lg shadow-inner border border-gray-200 overflow-hidden">
                    <div ref={canvasContainerRef} className="flex-grow flex items-top pt-20 justify-center p-4 bg-gray-200 overflow-auto relative">
                        {isSaving && <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20"><p className="text-blue-600 font-semibold">Saving design...</p></div>}
                        <div className="relative w-min h-min shadow-lg">
                            <canvas ref={canvasRef} />
                        </div>
                    </div>
                </section>
                <aside className="col-span-12 lg:col-span-4 flex flex-col gap-6 overflow-y-auto pb-4">
                    <ControlCard title="Pricing & Specs">
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-sm"><span className="text-gray-600">Card Size:</span><span className="font-medium text-gray-800">{cardConfigs[cardOrientation]?.label}</span></div>
                            <hr />
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Base Cost (Card):</span><span className="font-medium text-gray-800">₹{baseCost.toFixed(2)}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Print Cost (Design):</span><span className="font-medium text-gray-800">₹{printCost.toFixed(2)}</span></div>
                            <hr className="my-2 border-dashed" />
                            <div className="flex justify-between items-center text-lg font-bold text-blue-800"><span>Total Price:</span><span>₹{totalPrice.toFixed(2)}</span></div>
                        </div>
                    </ControlCard>
                    <ControlCard title="Design Tools">
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={addText} className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Add Text</button>
                            <button onClick={deleteSelected} className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Delete Selected</button>
                            <button onClick={clearCanvasContent} className="col-span-2 px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Clear All Content</button>
                        </div>
                    </ControlCard>
                    
                    {/* SAVE PRODUCT BUTTON */}
                    <ControlCard title="Finalize & Save">
                        <button
                            onClick={handleSaveProduct}
                            disabled={!librariesLoaded || isSaving || loading || !productName.trim() || !(!!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false))}
                            className="w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                        >
                            {isSaving ? 'Saving...' : 'Save Thank You Card'}
                        </button>
                        {!auth.currentUser && <p className="text-xs text-red-500 mt-2 text-center">Please log in to save.</p>}
                    </ControlCard>

                    {/* <ControlCard title="Export Options (Local)"> ... </ControlCard> */}
                </aside>
            </main>
        </div>
    );
};

export default ThankYouCardEditor;